import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, BadgeCheck, Building2, Phone, Mail, X, Leaf } from 'lucide-react';
import Navbar from '../components/Navbar';

export default function OurProcessPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showBuyerModal, setShowBuyerModal] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const valueChainSteps = [
    {
      icon: '🌾', title: 'Farm Sourcing', titleHi: 'किसानों से संग्रह',
      desc: 'Drone-monitored procurement from farmers',
      image: 'https://images.pexels.com/photos/34182314/pexels-photo-34182314.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      icon: '✅', title: 'AI Quality Check', titleHi: 'AI गुणवत्ता जांच',
      desc: 'AI-powered grading & robotic sorting',
      image: 'https://images.unsplash.com/photo-1627920769842-6887c6df05ca?w=600'
    },
    {
      icon: '🚛', title: 'Smart Logistics', titleHi: 'स्मार्ट परिवहन',
      desc: 'IoT-tracked cold chain delivery',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600'
    },
    {
      icon: '🏪', title: 'Buyer Connect', titleHi: 'खरीदार कनेक्ट',
      desc: 'Direct marketplace for hotels & exporters',
      image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=600'
    }
  ];

  return (
    <div className="page-wrapper">
      <Navbar />
      <div className="page-content">
        <section className="value-chain-section" id="value-chain">
          <div className="container">
            <div className="section-header-centered">
              <div className="section-badge">OUR PROCESS</div>
              <h2>How We Transform the Value Chain</h2>
              <h3 className="hindi-text">हम मूल्य श्रृंखला को कैसे बदलते हैं</h3>
            </div>

            <div className="value-chain-flow">
              <div className="flow-track">
                <div className="flow-items">
                  {valueChainSteps.map((step, index) => (
                    <div key={index} className={`flow-card ${activeStep === index ? 'active' : ''}`} onClick={() => setActiveStep(index)}>
                      <div className="flow-card-inner">
                        <div className="flow-number">{index + 1}</div>
                        <div className="flow-icon">{step.icon}</div>
                        <h4>{step.title}</h4>
                        <p className="hindi-small">{step.titleHi}</p>
                        <p className="flow-desc">{step.desc}</p>
                      </div>
                      {index < 3 && <div className="flow-connector"><ChevronRight size={32} /></div>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flow-detail">
                <div className="flow-detail-image">
                  <img src={valueChainSteps[activeStep].image} alt={valueChainSteps[activeStep].title} key={activeStep} />
                  <div className="image-overlay"><span className="step-badge">Step {activeStep + 1}</span></div>
                </div>
                <div className="flow-detail-content">
                  <div className="detail-icon">{valueChainSteps[activeStep].icon}</div>
                  <h3>{valueChainSteps[activeStep].title}</h3>
                  <h4>{valueChainSteps[activeStep].titleHi}</h4>
                  <p>{valueChainSteps[activeStep].desc}</p>
                  <div className="step-progress">
                    {valueChainSteps.map((_, idx) => (
                      <div key={idx} className={`progress-dot ${idx === activeStep ? 'active' : ''} ${idx < activeStep ? 'completed' : ''}`} onClick={() => setActiveStep(idx)} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="value-props">
              <div className="value-prop farmer-prop">
                <div className="prop-icon">👨‍🌾</div>
                <h4>For Farmers</h4>
                <h5>किसानों के लिए</h5>
                <ul>
                  <li><BadgeCheck size={16} /> Fair prices, direct payment</li>
                  <li><BadgeCheck size={16} /> No middlemen exploitation</li>
                  <li><BadgeCheck size={16} /> Assured crop purchase</li>
                </ul>
              </div>
              <div className="value-prop platform-prop">
                <div className="prop-logo"><Leaf size={32} /><span>BoAI</span></div>
                <p>Bridging the gap between farms and markets</p>
                <p className="hindi-small">खेतों और बाजारों के बीच की दूरी को कम करना</p>
              </div>
              <div className="value-prop buyer-prop">
                <div className="prop-icon">🏢</div>
                <h4>For Buyers</h4>
                <h5>खरीदारों के लिए</h5>
                <ul>
                  <li><BadgeCheck size={16} /> Premium quality produce</li>
                  <li><BadgeCheck size={16} /> Consistent supply chain</li>
                  <li><BadgeCheck size={16} /> Traceable sourcing</li>
                </ul>
              </div>
            </div>

            <div className="buyer-cta-banner">
              <div className="buyer-cta-content">
                <Building2 size={32} />
                <div className="buyer-cta-text">
                  <h4>Are you a Buyer? (Hotels, Retailers, Exporters)</h4>
                  <p>Get premium quality farm produce at competitive prices</p>
                </div>
                <button className="buyer-cta-btn" onClick={() => setShowBuyerModal(true)} data-testid="buyer-contact-btn">
                  Click Here to Contact Us
                </button>
              </div>
            </div>
          </div>
        </section>

        {showBuyerModal && (
          <div className="modal-overlay" onClick={() => setShowBuyerModal(false)}>
            <div className="buyer-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowBuyerModal(false)}><X size={24} /></button>
              <div className="modal-header">
                <div className="modal-icon">🏢</div>
                <h2>Partner with BoAI</h2>
                <h3>खरीदारों के लिए संपर्क करें</h3>
              </div>
              <div className="modal-body">
                <p className="modal-intro"><strong>Welcome, Valued Buyer!</strong></p>
                <p>At BoAI, we connect you directly with verified farmers across India, ensuring you receive <strong>fresh, high-quality agricultural produce</strong> at competitive prices.</p>
                <div className="modal-benefits">
                  <div className="benefit">Premium quality produce</div>
                  <div className="benefit">Consistent & reliable supply</div>
                  <div className="benefit">Competitive pricing</div>
                  <div className="benefit">Traceable farm-to-fork</div>
                </div>
                <div className="modal-contact">
                  <h4>Contact Our Team</h4>
                  <div className="contact-items">
                    <a href="tel:9599153699" className="contact-item"><Phone size={20} /><span>9599153699</span></a>
                    <a href="tel:8588828970" className="contact-item"><Phone size={20} /><span>8588828970</span></a>
                    <a href="mailto:siddharth.s@boai.in" className="contact-item email"><Mail size={20} /><span>siddharth.s@boai.in</span></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
